import { useEffect, useState, useMemo } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  LogOut,
  Mail,
  Phone,
  Building2,
  Calendar,
  RefreshCw,
  Trash2,
  Check,
  Search,
  Inbox,
} from "lucide-react";
import { useAdminAuth, adminApi } from "../../contexts/AdminAuthContext";

const SERVICE_LABELS = {
  muhasebe: "Muhasebe",
  vergi: "Vergi Danışmanlığı",
  sgk: "SGK",
  bordro: "Bordro",
  rapor: "Finansal Raporlama",
  butce: "Bütçe Yönetimi",
  kdv: "KDV İadesi",
  edefter: "e-Defter & e-Fatura",
};

const AdminDashboard = () => {
  const { admin, logout, loading } = useAdminAuth();
  const [submissions, setSubmissions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | unread | read

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.get("/admin/contact-submissions");
      setSubmissions(data);
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return submissions.filter((s) => {
      if (filter === "unread" && s.is_read) return false;
      if (filter === "read" && !s.is_read) return false;
      if (!q) return true;
      return [s.name, s.email, s.company, s.phone, s.message]
        .filter(Boolean)
        .some((f) => f.toLowerCase().includes(q));
    });
  }, [submissions, query, filter]);

  useEffect(() => {
    if (admin) load();
  }, [admin]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const unreadCount = submissions.filter((s) => !s.is_read).length;

  const toggleRead = async (id, newVal) => {
    try {
      const { data } = await adminApi.patch(`/admin/contact-submissions/${id}`, {
        is_read: newVal,
      });
      setSubmissions((prev) => prev.map((s) => (s.id === id ? data : s)));
      if (selected?.id === id) setSelected(data);
    } catch (e) {
      console.error(e);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu mesajı silmek istediğinize emin misiniz?")) return;
    try {
      await adminApi.delete(`/admin/contact-submissions/${id}`);
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (e) {
      console.error(e);
    }
  };

  const formatDate = (iso) =>
    new Date(iso).toLocaleString("tr-TR", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div className="min-h-screen bg-rotam-bg" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-white border-b border-rotam-border sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-heading text-xl text-rotam-primary font-medium">
              ROTAM
            </Link>
            <span className="text-[10px] tracking-[0.25em] uppercase text-rotam-muted">
              Admin Panel
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-sm text-rotam-muted">{admin.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="flex items-center gap-2 text-sm text-rotam-primary hover:text-rotam-primary-hover border border-rotam-primary/30 px-3 py-1.5 rounded-sm"
            >
              <LogOut size={14} /> Çıkış
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Header row */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-rotam-ink">
              İletişim Mesajları
            </h1>
            <p className="mt-2 text-sm text-rotam-muted">
              Toplam {submissions.length} mesaj · {unreadCount} okunmamış
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-rotam-muted"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="admin-search"
                placeholder="Ara: ad, e-posta, içerik..."
                className="pl-9 pr-3 py-2 text-sm border border-rotam-border rounded-sm bg-white focus:outline-none focus:border-rotam-primary w-64"
              />
            </div>
            <div className="flex bg-white border border-rotam-border rounded-sm overflow-hidden">
              {[
                { k: "all", label: "Tümü" },
                { k: "unread", label: "Okunmamış" },
                { k: "read", label: "Okunmuş" },
              ].map((f) => (
                <button
                  key={f.k}
                  onClick={() => setFilter(f.k)}
                  data-testid={`filter-${f.k}`}
                  className={`px-3 py-2 text-xs tracking-wide ${
                    filter === f.k
                      ? "bg-rotam-primary text-white"
                      : "text-rotam-ink hover:bg-rotam-bg"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <button
              onClick={load}
              disabled={busy}
              data-testid="admin-refresh"
              className="p-2 border border-rotam-border rounded-sm bg-white hover:bg-rotam-bg disabled:opacity-50"
              title="Yenile"
            >
              <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-12 gap-6">
          {/* List */}
          <div className="lg:col-span-5 space-y-3">
            {filtered.length === 0 ? (
              <div
                data-testid="admin-empty"
                className="bg-white border border-rotam-border rounded-sm p-12 text-center"
              >
                <Inbox size={32} className="mx-auto text-rotam-muted/60" />
                <p className="mt-4 text-sm text-rotam-muted">
                  {busy ? "Yükleniyor..." : "Henüz mesaj yok."}
                </p>
              </div>
            ) : (
              filtered.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSelected(s)}
                  data-testid={`submission-card-${s.id}`}
                  className={`w-full text-left bg-white border rounded-sm p-5 transition-all ${
                    selected?.id === s.id
                      ? "border-rotam-primary shadow-sm"
                      : "border-rotam-border hover:border-rotam-primary/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {!s.is_read && (
                          <span className="w-2 h-2 bg-rotam-accent rounded-full shrink-0" />
                        )}
                        <h3 className="font-heading text-base font-medium text-rotam-ink truncate">
                          {s.name}
                        </h3>
                      </div>
                      <p className="mt-1 text-xs text-rotam-muted truncate">{s.email}</p>
                      <p className="mt-2 text-sm text-rotam-ink/80 line-clamp-2">{s.message}</p>
                    </div>
                    <span className="text-[10px] tracking-wide text-rotam-muted whitespace-nowrap shrink-0">
                      {formatDate(s.created_at)}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Detail */}
          <div className="lg:col-span-7">
            {selected ? (
              <div
                data-testid="submission-detail"
                className="bg-white border border-rotam-border rounded-sm p-8 md:p-10 sticky top-24"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-2xl font-medium text-rotam-ink">
                      {selected.name}
                    </h2>
                    <p className="mt-1 text-xs tracking-wide uppercase text-rotam-accent">
                      {formatDate(selected.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleRead(selected.id, !selected.is_read)}
                      data-testid="toggle-read"
                      className={`flex items-center gap-2 text-xs px-3 py-2 rounded-sm border ${
                        selected.is_read
                          ? "bg-rotam-bg border-rotam-border text-rotam-muted"
                          : "bg-rotam-primary border-rotam-primary text-white"
                      }`}
                    >
                      <Check size={12} /> {selected.is_read ? "Okundu" : "Okundu işaretle"}
                    </button>
                    <button
                      onClick={() => remove(selected.id)}
                      data-testid="delete-submission"
                      className="p-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-sm"
                      title="Sil"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-4 text-sm border-y border-rotam-border py-6">
                  <div>
                    <dt className="text-[10px] tracking-[0.2em] uppercase text-rotam-muted">
                      E-posta
                    </dt>
                    <dd className="mt-1 flex items-center gap-2">
                      <Mail size={14} className="text-rotam-primary" />
                      <a
                        href={`mailto:${selected.email}`}
                        className="text-rotam-ink hover:text-rotam-primary truncate"
                      >
                        {selected.email}
                      </a>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] tracking-[0.2em] uppercase text-rotam-muted">
                      Telefon
                    </dt>
                    <dd className="mt-1 flex items-center gap-2">
                      <Phone size={14} className="text-rotam-primary" />
                      {selected.phone ? (
                        <a
                          href={`tel:${selected.phone}`}
                          className="text-rotam-ink hover:text-rotam-primary"
                        >
                          {selected.phone}
                        </a>
                      ) : (
                        <span className="text-rotam-muted">—</span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] tracking-[0.2em] uppercase text-rotam-muted">
                      Şirket
                    </dt>
                    <dd className="mt-1 flex items-center gap-2">
                      <Building2 size={14} className="text-rotam-primary" />
                      <span className="text-rotam-ink">{selected.company || "—"}</span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] tracking-[0.2em] uppercase text-rotam-muted">
                      İlgilendiği Hizmet
                    </dt>
                    <dd className="mt-1 text-rotam-ink">
                      {SERVICE_LABELS[selected.service] || selected.service || "—"}
                    </dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-[10px] tracking-[0.2em] uppercase text-rotam-muted">
                      Dil
                    </dt>
                    <dd className="mt-1 flex items-center gap-2">
                      <Calendar size={14} className="text-rotam-primary" />
                      <span className="text-rotam-ink uppercase">{selected.lang}</span>
                    </dd>
                  </div>
                </dl>

                <div className="mt-6">
                  <dt className="text-[10px] tracking-[0.2em] uppercase text-rotam-muted mb-3">
                    Mesaj
                  </dt>
                  <p className="text-rotam-ink whitespace-pre-wrap leading-relaxed">
                    {selected.message}
                  </p>
                </div>

                <div className="mt-8 pt-6 border-t border-rotam-border flex gap-3">
                  <a
                    href={`mailto:${selected.email}?subject=Re: ROTAM iletişim formu`}
                    className="btn-primary"
                  >
                    <Mail size={14} /> Yanıtla
                  </a>
                  {selected.phone && (
                    <a href={`tel:${selected.phone}`} className="btn-ghost">
                      <Phone size={14} /> Ara
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-rotam-border border-dashed rounded-sm p-16 text-center">
                <Inbox size={32} className="mx-auto text-rotam-muted/60" />
                <p className="mt-4 text-sm text-rotam-muted">
                  Detayları görmek için bir mesaj seçin.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
